import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { AlertCircle, Loader2 } from 'lucide-react';
import { AuthLayout } from '../../layouts/AuthLayout';
import { AppCard } from '../../components/ui/AppCard';
import { EmptyState } from '../../components/ui/EmptyState';
import { Button } from '../../components/ui/Button';
import { completeOAuthSignIn } from '../../api/authApi';
import { useStore } from '../../store/StoreContext';

// FRONTEND_URL/oauth/callback/{provider} - the single redirect_uri
// registered with all four providers (see backend/modules/oauth/
// transitions/oauth.go's header comment). Every provider lands here with
// either `?code=&state=` (success) or `?error=...` (the visitor cancelled
// or denied consent) - this screen's only job is to hand code+state to
// POST /oauth/exchange and, on success, log in exactly like a password
// login (StoreContext's loginWithToken).
export function OAuthCallbackScreen() {
  const { provider } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { loginWithToken } = useStore();
  const [error, setError] = useState('');
  // StrictMode's dev-only double-render would otherwise run this effect's
  // one-time-use state consumption twice - see App.tsx's RequireGuest for
  // the identical bug/fix this session already hit once.
  const startedRef = useRef(false);

  useEffect(() => {
    if (startedRef.current) return;
    startedRef.current = true;

    const run = async () => {
      if (!provider) {
        setError('Missing sign-in provider.');
        return;
      }

      const providerError = searchParams.get('error');
      if (providerError) {
        setError('Sign-in was cancelled.');
        return;
      }

      const code = searchParams.get('code');
      const state = searchParams.get('state');
      const expectedState = sessionStorage.getItem('oauth.state');
      const expectedProvider = sessionStorage.getItem('oauth.provider');
      sessionStorage.removeItem('oauth.state');
      sessionStorage.removeItem('oauth.provider');

      if (!code || !state) {
        setError('This sign-in link is missing required information.');
        return;
      }
      // Confirms the redirect landing here was actually started by this
      // browser (not a forged/replayed callback URL) before ever spending
      // the one-time server-side state token on it - see oauth.go's
      // consumeState comment for the server-side half of this check.
      if (state !== expectedState || provider !== expectedProvider) {
        setError('This sign-in link is invalid or was already used. Please try again.');
        return;
      }

      const result = await completeOAuthSignIn(provider, code, state);
      if (!result.success || !result.token || !result.email) {
        setError(result.error ?? 'Unable to sign in.');
        return;
      }
      // No explicit navigate() here - flipping isLoggedIn is enough. This
      // screen is rendered inside App.tsx's RequireGuest, same as /login,
      // which already redirects the moment isLoggedIn goes true (to
      // wherever rememberPostLoginRedirect stashed, e.g. back to a shared
      // channel's Join flow, or /channels by default).
      loginWithToken(result.email);
    };

    run();
  }, [provider, searchParams, loginWithToken]);

  return (
    <AuthLayout>
      <AppCard>
        {error ? (
          <div className="flex flex-col items-center gap-4">
            <EmptyState icon={AlertCircle} title="Sign-in didn't work" subtitle={error} />
            <Button full onClick={() => navigate('/login')}>
              Back to login
            </Button>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-3 py-10 text-center">
            <Loader2 size={28} className="animate-spin text-black/40 dark:text-white/40" />
            <span className="text-sm text-black/50 dark:text-white/50">Finishing sign-in…</span>
          </div>
        )}
      </AppCard>
    </AuthLayout>
  );
}
